import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CreditCard, Shield, CheckCircle2, Store, MessageCircle, Send } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { DynIcon } from "@/lib/icons";

function InfoCard({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-[#1C1C1C] border border-[#333] rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <Icon size={26} className="text-[#E60000] shrink-0" />
        <h3 className="font-heading font-bold uppercase text-xl text-[#F0EDEA]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { settings } = useStore();
  const s = settings || {};
  const methods = (s.payment_methods || []).filter((m) => m.enabled);

  const ActionButtons = ({ suffix }) => (
    <div className="w-full space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <a href={s.signal_link || "#"} target="_blank" rel="noreferrer" className="btn-skew btn-secondary w-full" data-testid={`signal-btn-${suffix}`}>
          <span><MessageCircle size={18} /> Signal</span>
        </a>
        <a href={s.telegram_link || "#"} target="_blank" rel="noreferrer" className="btn-skew btn-secondary w-full" data-testid={`telegram-btn-${suffix}`}>
          <span><Send size={18} /> Telegram</span>
        </a>
      </div>
      <button onClick={() => navigate("/products")} className="btn-skew btn-primary w-full" data-testid={`view-menu-btn-${suffix}`}>
        <span><Store size={18} /> View Menu</span>
      </button>
    </div>
  );

  return (
    <div className="px-4 py-8 space-y-8">
      {/* Hero */}
      <section className="flex flex-col items-center text-center gap-5 pt-4 fade-up">
        <div className="eyebrow text-sm">Welcome To</div>
        <h1 className="font-heading font-black italic text-6xl sm:text-7xl text-[#E60000] leading-none red-glow" data-testid="hero-title">
          RED EYE
        </h1>
        <p className="text-[#F0EDEA] max-w-md text-base leading-relaxed" data-testid="hero-tagline">
          {s.homepage_tagline || "Premium selection, unbeatable prices. Message us on Signal or Telegram to order."}
        </p>
        <ActionButtons suffix="top" />
      </section>

      {/* Hours */}
      <InfoCard icon={Clock} title="Hours" testid="hours-card">
        <p className="text-[#F0EDEA] font-semibold whitespace-pre-line">{s.hours || "Mon-Sun: 10AM - 10PM"}</p>
      </InfoCard>

      {/* Payments Accepted */}
      <InfoCard icon={CreditCard} title="Payments Accepted" testid="payments-card">
        <div className="grid grid-cols-2 gap-3">
          {methods.map((m) => (
            <div key={m.id} className="flex items-center gap-2 bg-[#141414] border border-[#2a2a2a] rounded-lg px-3 py-3" data-testid={`payment-info-${m.id}`}>
              <DynIcon name={m.icon} size={18} className="text-[#E60000] shrink-0" />
              <span className="text-[#F0EDEA] text-sm font-semibold">{m.name} - {m.fee_pct}%</span>
            </div>
          ))}
        </div>
      </InfoCard>

      {/* Why RED EYE */}
      <InfoCard icon={Shield} title="Why RED EYE" testid="why-card">
        <ul className="space-y-3">
          {(s.why_list || []).map((item, i) => (
            <li key={i} className="flex items-center gap-3 text-[#F0EDEA]" data-testid={`why-item-${i}`}>
              <CheckCircle2 size={22} className="text-[#E60000] shrink-0" fill="#E60000" stroke="#111" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </InfoCard>

      {/* Repeated actions */}
      <section className="pt-2">
        <ActionButtons suffix="bottom" />
      </section>
    </div>
  );
}
